import './LossCurve.css';

export interface LossCurveProps {
  /** Per-epoch loss, oldest first. */
  lossHistory: number[];
  /** Optional final accuracy (0..1) — shown alongside the curve. */
  finalAccuracy?: number;
  width?: number;
  height?: number;
}

/**
 * "Under the hood" loss curve. Pure SVG, no chart library — the curve
 * is a polyline in a viewBox, axis labels are tiny `<text>` nodes. This
 * keeps the platform shell light and renders crisply at any DPI without
 * reaching for a charting dep.
 *
 * The kid sees how the model's mistakes shrink each round of training.
 * If the line keeps dropping at the right edge, the model was still
 * improving when training stopped — the convergence pip in the train
 * panel says "still learning" for the same reason.
 */
export function LossCurve({
  lossHistory,
  finalAccuracy,
  width = 360,
  height = 140,
}: LossCurveProps): JSX.Element {
  if (lossHistory.length === 0) {
    return (
      <div className="bl-loss-curve bl-loss-curve--empty" role="img" aria-label="No training data">
        <p>No training data to show yet.</p>
      </div>
    );
  }

  const padding = { top: 12, right: 18, bottom: 24, left: 32 };
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;

  const min = Math.min(...lossHistory);
  const max = Math.max(...lossHistory);
  // Force a non-zero range so a perfectly flat history still draws.
  const range = max - min < 1e-6 ? Math.max(1e-3, Math.abs(max)) : max - min;
  const xStep = lossHistory.length > 1 ? innerWidth / (lossHistory.length - 1) : 0;

  const points = lossHistory
    .map((loss, i) => {
      const x = padding.left + i * xStep;
      const y = padding.top + ((max - loss) / range) * innerHeight;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  const finalLoss = lossHistory[lossHistory.length - 1] ?? 0;

  return (
    <div className="bl-loss-curve">
      <p className="bl-loss-curve__title">Loss over training</p>
      <svg
        className="bl-loss-curve__svg"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`Loss curve across ${lossHistory.length} training epochs`}
      >
        {/* Axes */}
        <line
          x1={padding.left}
          y1={padding.top}
          x2={padding.left}
          y2={padding.top + innerHeight}
          className="bl-loss-curve__axis"
        />
        <line
          x1={padding.left}
          y1={padding.top + innerHeight}
          x2={padding.left + innerWidth}
          y2={padding.top + innerHeight}
          className="bl-loss-curve__axis"
        />
        {/* Curve */}
        <polyline points={points} className="bl-loss-curve__line" />
        {/* End-point dot */}
        <circle
          cx={padding.left + (lossHistory.length - 1) * xStep}
          cy={padding.top + ((max - finalLoss) / range) * innerHeight}
          r={3}
          className="bl-loss-curve__dot"
        />
        {/* y-axis labels */}
        <text
          x={padding.left - 6}
          y={padding.top + 4}
          textAnchor="end"
          className="bl-loss-curve__label"
        >
          {max.toFixed(2)}
        </text>
        <text
          x={padding.left - 6}
          y={padding.top + innerHeight}
          textAnchor="end"
          className="bl-loss-curve__label"
        >
          {min.toFixed(2)}
        </text>
        {/* x-axis labels */}
        <text
          x={padding.left}
          y={padding.top + innerHeight + 16}
          textAnchor="start"
          className="bl-loss-curve__label"
        >
          1
        </text>
        <text
          x={padding.left + innerWidth}
          y={padding.top + innerHeight + 16}
          textAnchor="end"
          className="bl-loss-curve__label"
        >
          {lossHistory.length}
        </text>
      </svg>
      <p className="bl-loss-curve__summary">
        Final loss <strong>{finalLoss.toFixed(3)}</strong>
        {finalAccuracy !== undefined && (
          <>
            {' '}
            · training accuracy{' '}
            <strong>{Math.round(Math.max(0, Math.min(1, finalAccuracy)) * 100)}%</strong>
          </>
        )}
      </p>
    </div>
  );
}
